import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Archive, RotateCcw, Download, AlertTriangle, ShieldCheck, Building2, TrendingUp, Send, GitMerge } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Forensic archive — three tabs (deleted clubs, deleted members, purged
 * rosters). Clubs can be RESTORED in-place (full data round-trip if the
 * snapshot is v2). Members + rosters are read-only browse with JSON
 * download — restore for those is rare and the JSON is enough.
 */
export default function SuperAdminArchivePage() {
  const [tab, setTab] = useState("clubs");
  const [clubs, setClubs] = useState(null);
  const [members, setMembers] = useState(null);
  const [rosters, setRosters] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const loadAll = () => {
    Promise.all([
      api.get("/super-admin/archive/deleted-clubs"),
      api.get("/super-admin/archive/deleted-members"),
      api.get("/super-admin/archive/purged-rosters"),
    ])
      .then(([c, m, r]) => {
        setClubs(c.data.items);
        setMembers(m.data.items);
        setRosters(r.data.items);
      })
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message));
  };
  useEffect(loadAll, []);

  const downloadJson = async (path, filename) => {
    try {
      const { data } = await api.get(path);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const restoreClub = async (item) => {
    const { snapshot_id, club, restorable } = item;
    if (!restorable) {
      window.alert(
        "This snapshot is a legacy counts-only record (v1). Use Download JSON to inspect what was deleted — automated restore isn't possible.",
      );
      return;
    }
    const ok = window.confirm(
      `Restore ${club.name} (${club.slug})?\n\n` +
      "This will re-create the club along with its orders, wishlist, photos, audit log, " +
      "members and admin shells from the snapshot. The snapshot is then consumed so the same " +
      "restore can't be replayed.",
    );
    if (!ok) return;
    setBusyId(snapshot_id);
    try {
      const { data } = await api.post(`/super-admin/archive/deleted-clubs/${snapshot_id}/restore`);
      window.alert(`Restored.\n\n${JSON.stringify(data.restored, null, 2)}`);
      loadAll();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-cream min-h-screen" data-testid="sa-archive-page">
      <header className="bg-[#0f1a14] text-[#F9F8F4] border-b border-[#1A422D]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Archive size={22} className="text-[#D9C68A]" />
            <div>
              <div className="overline" style={{ color: "#D9C68A" }}>Platform</div>
              <div className="font-serif text-2xl mt-0.5">Forensic Archive</div>
            </div>
          </div>
          <Link
            to="/super-admin"
            data-testid="sa-archive-back"
            className="inline-flex items-center gap-2 border border-[#D9C68A]/50 text-[#D9C68A] px-3 py-1.5 text-xs uppercase tracking-[0.22em] hover:bg-[#1A422D] transition-colors"
          >
            <ShieldCheck size={14} /> Back to Console
          </Link>
        </div>
      </header>

      <nav className="bg-[#142019] border-b border-[#1A422D]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-3 flex items-center gap-3 flex-wrap">
          <Link to="/super-admin/clubs" data-testid="sa-nav-clubs" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#D9C68A]/80 hover:text-[#D9C68A]"><Building2 size={12} /> Clubs</Link>
          <span className="text-[#1A422D]">·</span>
          <Link to="/super-admin/demand" data-testid="sa-nav-demand" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#D9C68A]/80 hover:text-[#D9C68A]"><TrendingUp size={12} /> Demand</Link>
          <span className="text-[#1A422D]">·</span>
          <Link to="/super-admin/invitations" data-testid="sa-nav-invitations" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#D9C68A]/80 hover:text-[#D9C68A]"><Send size={12} /> Invitations</Link>
          <span className="text-[#1A422D]">·</span>
          <Link to="/super-admin/merge" data-testid="sa-nav-merge" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#D9C68A]/80 hover:text-[#D9C68A]"><GitMerge size={12} /> Merge &amp; Transfer</Link>
          <span className="text-[#1A422D]">·</span>
          <Link to="/super-admin/archive" data-testid="sa-nav-archive" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#D9C68A]"><Archive size={12} /> Archive</Link>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 space-y-8">
        {error && (
          <div className="px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">{error}</div>
        )}

        <div>
          <div className="overline">Deleted &amp; Purged</div>
          <h2 className="font-serif text-3xl text-[#1A1A1A] mt-1">Forensic Snapshots</h2>
          <p className="text-sm text-[#4A4A4A] mt-2 max-w-2xl">
            Every destructive Platform Administrator action drops a snapshot here. Restore deleted clubs in place,
            or download the raw JSON for the rare member / roster recovery.
          </p>
        </div>

        <div className="flex items-center gap-2 border-b border-[#E5E3D8]">
          <TabBtn id="clubs" active={tab} onClick={setTab} testId="sa-archive-tab-clubs">
            Deleted Clubs · {clubs?.length ?? "—"}
          </TabBtn>
          <TabBtn id="members" active={tab} onClick={setTab} testId="sa-archive-tab-members">
            Deleted Members · {members?.length ?? "—"}
          </TabBtn>
          <TabBtn id="rosters" active={tab} onClick={setTab} testId="sa-archive-tab-rosters">
            Purged Rosters · {rosters?.length ?? "—"}
          </TabBtn>
        </div>

        {tab === "clubs" && (
          <section data-testid="sa-archive-pane-clubs" className="surface-card overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-[#F3F1E9]">
                <tr className="text-xs uppercase tracking-wider text-[#4A4A4A]">
                  <th className="px-6 py-4">Club</th>
                  <th className="px-6 py-4">Deleted</th>
                  <th className="px-6 py-4">By</th>
                  <th className="px-6 py-4">Counts</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {clubs && clubs.length === 0 && (
                  <tr><td colSpan={5} className="px-6 py-10 text-center text-[#7A7A7A]">No deleted clubs.</td></tr>
                )}
                {(clubs || []).map((item) => (
                  <tr key={item.snapshot_id} className="border-t border-[#E5E3D8]" data-testid={`sa-archive-club-row-${item.snapshot_id}`}>
                    <td className="px-6 py-4">
                      <div className="font-serif text-base text-[#1A1A1A]">{item.club?.name || "—"}</div>
                      <div className="text-xs text-[#7A7A7A] font-mono">{item.club?.slug}</div>
                      {!item.restorable && (
                        <div className="mt-1.5 inline-flex items-center gap-1 text-xs text-[#8a6f33]">
                          <AlertTriangle size={11} /> Legacy snapshot (counts only)
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#4A4A4A]">{(item.deleted_at || "").replace("T", " ").slice(0, 19)}</td>
                    <td className="px-6 py-4 text-sm">{item.deleted_by_email || "—"}</td>
                    <td className="px-6 py-4 text-xs">
                      <div className="text-[#7A7A7A] space-y-0.5">
                        {Object.entries(item.counts || {}).slice(0, 4).map(([k, v]) => (
                          <div key={k}><span className="font-mono">{v}</span> {k.replace(/_/g, " ")}</div>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-3 justify-end">
                        <button
                          type="button"
                          onClick={() => downloadJson(`/super-admin/archive/deleted-clubs/${item.snapshot_id}`, `${item.club?.slug || "club"}-${item.snapshot_id}.json`)}
                          data-testid={`sa-archive-club-download-${item.snapshot_id}`}
                          className="text-xs text-[#1A422D] underline underline-offset-4 inline-flex items-center gap-1.5"
                        >
                          <Download size={12} /> JSON
                        </button>
                        <button
                          type="button"
                          onClick={() => restoreClub(item)}
                          disabled={!item.restorable || busyId === item.snapshot_id}
                          data-testid={`sa-archive-club-restore-${item.snapshot_id}`}
                          className="px-3 py-1.5 text-xs uppercase tracking-[0.18em] font-semibold bg-[#1A422D] hover:bg-[#0f3220] text-[#F9F8F4] transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                        >
                          <RotateCcw size={12} />
                          {busyId === item.snapshot_id ? "Restoring…" : "Restore"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {tab === "members" && (
          <section data-testid="sa-archive-pane-members" className="surface-card overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-[#F3F1E9]">
                <tr className="text-xs uppercase tracking-wider text-[#4A4A4A]">
                  <th className="px-6 py-4">Member</th>
                  <th className="px-6 py-4">Email</th>
                  <th className="px-6 py-4">Deleted</th>
                  <th className="px-6 py-4">By</th>
                  <th className="px-6 py-4 text-right">JSON</th>
                </tr>
              </thead>
              <tbody>
                {members && members.length === 0 && (
                  <tr><td colSpan={5} className="px-6 py-10 text-center text-[#7A7A7A]">No deleted members.</td></tr>
                )}
                {(members || []).map((item) => (
                  <tr key={item.snapshot_id || item.deleted_at} className="border-t border-[#E5E3D8]" data-testid={`sa-archive-member-row-${item.snapshot_id || ""}`}>
                    <td className="px-6 py-4">
                      <div className="font-serif text-base text-[#1A1A1A]">{item.user?.name || "—"}</div>
                      <div className="text-xs text-[#7A7A7A] font-mono">{item.user?.user_id}</div>
                    </td>
                    <td className="px-6 py-4 text-sm">{item.user?.email}</td>
                    <td className="px-6 py-4 text-sm text-[#4A4A4A]">{(item.deleted_at || "").replace("T", " ").slice(0, 19)}</td>
                    <td className="px-6 py-4 text-sm">{item.deleted_by_email || "—"}</td>
                    <td className="px-6 py-4 text-right">
                      {item.snapshot_id ? (
                        <button
                          type="button"
                          onClick={() => downloadJson(`/super-admin/archive/deleted-members/${item.snapshot_id}`, `member-${item.user?.email || item.snapshot_id}.json`)}
                          data-testid={`sa-archive-member-download-${item.snapshot_id}`}
                          className="text-xs text-[#1A422D] underline underline-offset-4 inline-flex items-center gap-1.5"
                        >
                          <Download size={12} /> JSON
                        </button>
                      ) : <span className="text-xs text-[#9A9A9A]">legacy</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {tab === "rosters" && (
          <section data-testid="sa-archive-pane-rosters" className="surface-card overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-[#F3F1E9]">
                <tr className="text-xs uppercase tracking-wider text-[#4A4A4A]">
                  <th className="px-6 py-4">Club</th>
                  <th className="px-6 py-4">Purged</th>
                  <th className="px-6 py-4">By</th>
                  <th className="px-6 py-4">Members</th>
                  <th className="px-6 py-4 text-right">JSON</th>
                </tr>
              </thead>
              <tbody>
                {rosters && rosters.length === 0 && (
                  <tr><td colSpan={5} className="px-6 py-10 text-center text-[#7A7A7A]">No roster purges yet.</td></tr>
                )}
                {(rosters || []).map((item) => (
                  <tr key={item.snapshot_id || item.purged_at} className="border-t border-[#E5E3D8]" data-testid={`sa-archive-roster-row-${item.snapshot_id || ""}`}>
                    <td className="px-6 py-4">
                      <div className="font-serif text-base text-[#1A1A1A]">{item.club_name || "—"}</div>
                      <div className="text-xs text-[#7A7A7A] font-mono">{item.club_slug}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#4A4A4A]">{(item.purged_at || "").replace("T", " ").slice(0, 19)}</td>
                    <td className="px-6 py-4 text-sm">{item.purged_by_email || "—"}</td>
                    <td className="px-6 py-4 text-xs">
                      <span className="font-mono">{item.counts?.members_processed ?? 0}</span> processed ·{" "}
                      <span className="font-mono">{item.counts?.users_fully_deleted ?? 0}</span> deleted
                    </td>
                    <td className="px-6 py-4 text-right">
                      {item.snapshot_id ? (
                        <button
                          type="button"
                          onClick={() => downloadJson(`/super-admin/archive/purged-rosters/${item.snapshot_id}`, `roster-${item.club_slug || item.snapshot_id}.json`)}
                          data-testid={`sa-archive-roster-download-${item.snapshot_id}`}
                          className="text-xs text-[#1A422D] underline underline-offset-4 inline-flex items-center gap-1.5"
                        >
                          <Download size={12} /> JSON
                        </button>
                      ) : <span className="text-xs text-[#9A9A9A]">legacy</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </div>
  );
}

function TabBtn({ id, active, onClick, children, testId }) {
  const is = active === id;
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      data-testid={testId}
      className={`px-5 py-3 text-xs uppercase tracking-[0.18em] transition-colors border-b-2 -mb-px ${
        is ? "border-[#1A422D] text-[#1A422D]" : "border-transparent text-[#7A7A7A] hover:text-[#1A1A1A]"
      }`}
    >
      {children}
    </button>
  );
}
